import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import Heading from '../../Components/Heading/heading.tsx';
import * as XLSX from 'xlsx';

interface BulkOrderModalProps {
  showModal: boolean;
  setShowModal:(state: boolean) => void;
  handleClose: () => void;
  setUploadPreviewModal:(state:boolean) => void;
  setSelectedFile: (file: File | null) => void;
  file:any;
  setTableData: (state:any) => void;
  setTableColumns: (state:any) => void;
}

const BulkOrderModal: React.FC<BulkOrderModalProps> = ({
  showModal,
  setShowModal,
  handleClose,
  setUploadPreviewModal,
  setSelectedFile,
  setTableData,
  setTableColumns,
  file
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCloseModal = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    handleClose(); 
  };

  const validateFileData = (data: unknown): data is any[][] => {
    if (!Array.isArray(data)) {
        setError("Invalid file format: Expected array data.");
        return false;
    }

    if (data.length <= 1) {
        setError("The File is Empty. Try Again");
        return false;
    }

    const hasData = data.slice(1).some(row => 
        Array.isArray(row) && 
        row.some(cell => cell !== null && cell !== undefined && cell !== '')
    );

    if (!hasData) {
        setError("The File is Empty. Try Again");
        return false;
    }

    return true;
};

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      const reader = new FileReader();
      
      reader.onload = (evt) => {
        try {
          const bstr = evt.target?.result;
          const wb = XLSX.read(bstr, { type: 'binary' });
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
          
          if (!validateFileData(jsonData)) {
            setSelectedFile(null);
            return;
          }
          
          setSelectedFile(selectedFile);
          processData(jsonData);
          setUploadPreviewModal(true);
          setShowModal(false);
        } catch (err) {
          setError("Error processing file. Please check the format and try again.");
          setSelectedFile(null);
        }
      };
      
      reader.onerror = () => {
        setError("Error reading file. Please try again.");
        setSelectedFile(null);
      };
      
      reader.readAsBinaryString(selectedFile);
    }
  };

  const processData = (dataString: any) => {
    const dataStringLines = dataString;
    const headers = dataStringLines[0];
    const list: Record<string, string>[] = [];
    
    for (let i = 1; i < dataStringLines.length; i++) {
      const row = dataStringLines[i];
      if (headers.length > 0) {
        const obj: Record<string, string> = {};
        for (let j = 0; j < headers.length; j++) {
          let d = row[j] || "";
          if (d.length > 0) {
            if (d[0] === '"')
              d = d.substring(1, d.length - 1);
            if (d[d.length - 1] === '"')
              d = d.substring(d.length - 2, 1);
          }
          if (headers[j]) {
            obj[headers[j]] = d;
          }
        }
        if (Object.values(obj).filter(x => x).length > 0) {
          list.push(obj);
        }
      }
    }

    const columns = headers.map((c: any) => ({
      label: c,
      field: c,
      sort: 'asc',
      width: 150
    }));

    setTableData(list);
    setTableColumns(columns);
  };

  const bulkUploadFormatUrl = `${global.bulk_upload_format}`;

  return (
    <Dialog open={showModal} onClose={handleClose} maxWidth="sm" fullWidth>
  <Box sx={{ marginLeft: '-180px'  }}>
      <Heading title="Upload Bulk Order" closeIcon="true" onClose={handleCloseModal} />
</Box>
      <DialogContent sx={{padding:'10px 24px 20px 20px'}}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" flexGrow={1} mr={2}>
            <Button
              variant="contained"
              component="label"
               className="custom-button"
              sx={{ textTransform: "none" }}
            >
              Choose File
              <input
                type="file"
                ref={fileInputRef}
                hidden
                onChange={handleFileChange}
                accept=".xlsx,.xls,.csv"
              />
            </Button>
            <Typography ml={2} sx={{ flexGrow: 1 }}>
              {file ? file.name : "No file chosen"}
            </Typography>
          </Box>

          <Button
            className="custom-cancel-button"
            href={bulkUploadFormatUrl}
            download="bulk_upload.csv"
            startIcon={<DownloadIcon />}
          >
            Format
          </Button>
        </Box>
        
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkOrderModal;